import type { DesignSpec } from "@/types/designSpec";
import type { GalleryDress } from "@/types/gallery";
import { mapPresetTypeToGalleryCategory } from "@/utils/galleryPresetMapping";

export const AMAZON_SEARCH_MAX_LENGTH = 300;

/** Only shopping attributes belong in an external search, never the full brief. */
export function buildDressSearchTerms(
  spec: DesignSpec | null | undefined
): string {
  const visual = spec?.visual;
  return buildShoppingQuery({
    color: visual?.primaryColor,
    silhouette: visual?.silhouette,
    neckline: visual?.neckline,
    sleeve: visual?.sleeve,
    fabric: visual?.fabric,
  });
}

/** Gallery searches use stored shopping attributes, never prompts or titles. */
export function buildGalleryDressSearchTerms(
  dress: Partial<GalleryDress>
): string {
  const category = mapPresetTypeToGalleryCategory(dress.preset_type);
  return buildShoppingQuery({
    color: dress.color,
    category: category === "all" ? undefined : category,
    silhouette: dress.silhouette_fit,
    neckline: dress.neckline_style,
    sleeve: dress.sleeve_style,
    fabric: dress.fabric_texture,
  });
}

// Legacy options contain explanatory sentences. Keep the label, normalize its
// separators, and bound each attribute so it cannot crowd out the dress type.
function shoppingLabel(value?: string | null): string {
  return (value ?? "")
    .replaceAll("_", " ")
    .replace(/[–—]/g, "-")
    .split(/\s+-\s+|[(:]/)[0]
    .toLowerCase()
    .trim()
    .split(/\s+/)
    .slice(0, 5)
    .join(" ")
    .slice(0, 60);
}

function shoppingSilhouette(value?: string | null): string {
  const label = shoppingLabel(value);
  if (/\ba[ -]?line\b/.test(label)) return "A-line";
  if (/\b(ball[ -]?gown|princess)\b/.test(label)) return "ball gown";
  if (/\bempire\b/.test(label)) return "empire waist";
  if (/\b(drop[ -]?waist|dropped waist)\b/.test(label)) return "drop waist";
  if (/\bfit(?: &| and)?[ -]flare\b/.test(label)) return "fit and flare";
  return label;
}

function keyFeature(fabric: string, sleeve: string, neckline: string): string {
  // One distinctive feature, not every attribute. Motifs outrank sleeve shape;
  // sleeve shape outranks the neckline and ordinary sleeve length.
  if (/\bbutterfl(?:y|ies)\b/.test(fabric)) return "butterfly applique";
  if (/\bfloral|\bflowers?\b/.test(fabric)) return "floral";
  if (/\bsequins?\b/.test(fabric)) return "sequin";
  if (/\bbead(?:ed|ing)?\b/.test(fabric)) return "beaded";
  const shapedSleeve = sleeve.match(
    /\b(bell|bishop|puff|puffed|flutter|cape)\b/
  );
  if (shapedSleeve) return `${shapedSleeve[1]} sleeves`;
  if (/\bone[ -]shoulder\b/.test(sleeve)) return "one shoulder";
  if (/\boff[ -]shoulder\b/.test(sleeve)) return "off shoulder";
  if (neckline) {
    if (/^(high|high neck|high neckline|mock neck)$/.test(neckline))
      return "high neck";
    if (/^(v|v[ -]neck|v[ -]neckline)$/.test(neckline)) return "V-neck";
    if (/^(heart|heart[ -]shaped|sweetheart)$/.test(neckline))
      return "sweetheart neckline";
    if (/\b(draped|cowl)\b/.test(neckline)) return "cowl neck";
    return /\b(neck|neckline|shoulder)\b/.test(neckline)
      ? neckline
      : `${neckline} neckline`;
  }
  if (sleeve === "sleeveless" || sleeve === "strapless") return sleeve;
  if (/\blong\b/.test(sleeve)) return "long sleeves";
  if (/\bshort\b/.test(sleeve)) return "short sleeves";
  return sleeve;
}

function buildShoppingQuery(attributes: {
  color?: string | null;
  category?: string;
  silhouette?: string | null;
  neckline?: string | null;
  sleeve?: string | null;
  fabric?: string | null;
}): string {
  const fabric = shoppingLabel(attributes.fabric);
  const material = fabric.match(
    /\b(satin|silk|mikado|organza|tulle|velvet|crepe|chiffon|lace|cotton|linen)\b/
  )?.[0];
  const color = shoppingLabel(attributes.color);
  return formatShoppingTerms([
    color === "pearl" ? "ivory" : color,
    material,
    attributes.category,
    shoppingSilhouette(attributes.silhouette),
    "dress",
    keyFeature(
      fabric,
      shoppingLabel(attributes.sleeve),
      shoppingLabel(attributes.neckline)
    ),
  ]);
}

function formatShoppingTerms(values: (string | null | undefined)[]): string {
  return [
    ...new Set(
      values.map(value => value?.replaceAll("_", " ").trim()).filter(Boolean)
    ),
  ]
    .join(" ")
    .replace(/\s+/g, " ")
    .slice(0, AMAZON_SEARCH_MAX_LENGTH)
    .trim();
}

/** Ignore malformed configuration rather than breaking ordinary search. */
export function normalizeAmazonAssociateTag(tag?: string): string | null {
  const value = tag?.trim();
  return value && /^[a-zA-Z0-9-]{1,100}-[0-9]{2}$/.test(value) ? value : null;
}

/** Search-result links support an optional Associates ID without a catalog API. */
export function buildAmazonSearchUrl(
  terms: string,
  associateTag?: string | null
): string | null {
  const query = terms.replace(/\s+/g, " ").trim();
  if (!query || query.length > AMAZON_SEARCH_MAX_LENGTH) return null;

  const url = new URL("https://www.amazon.com/s");
  url.searchParams.set("k", query);
  const tag = normalizeAmazonAssociateTag(associateTag ?? undefined);
  if (tag) url.searchParams.set("tag", tag);
  return url.toString();
}
